/* eslint-disable prettier/prettier */

import * as React from "react";
import {
  Grid,
  Link,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Tooltip,
  Box,
  SvgIcon,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CreateIcon from "@mui/icons-material/Create";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import DeleteIcon from "@mui/icons-material/Delete";

import { AuthenticationContext, ImagesContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import { useFlash } from "../FlashContext";
import { Modal as DeleteModal } from "../modals/Modal";

const NOTES_PREVIEW_LENGTH = 120;

const signedBadgeSx = {
  display: "flex",
  width: 100,
  padding: "0 8px",
  justifyContent: "center",
  alignItems: "center",
  flexShrink: 0,
  borderRadius: "10px",
  background: "#EDF7EE",
  color: "#07BC2A",
  fontFamily: "QuicksandMedium",
  fontSize: "10px",
  fontStyle: "normal",
  fontWeight: 100,
  lineHeight: "20px",
  letterSpacing: "0.4px",
};

const pendedBadgeSx = {
  display: "flex",
  width: 100,
  padding: "0 8px",
  justifyContent: "center",
  alignItems: "center",
  flexShrink: 0,
  borderRadius: "10px",
  background: "#FFF0BE",
  color: "#B08800",
  fontFamily: "QuicksandMedium",
  fontSize: "8px",
  fontStyle: "normal",
  fontWeight: 100,
  lineHeight: "20px",
  letterSpacing: "0.4px",
};

const archivedBadgeSx = {
  display: "flex",
  width: 100,
  padding: "0 8px",
  justifyContent: "center",
  alignItems: "center",
  flexShrink: 0,
  borderRadius: "10px",
  background: "#FFD9D9",
  color: "#F00",
  fontFamily: "QuicksandMedium",
  fontSize: "10px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "20px",
  letterSpacing: "0.4px",
};

const overviewDateSx = {
  color: "#898989",
  fontFamily: "QuicksandMedium",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "18px",
};

const overviewTypeSx = {
  color: "#1E1E1E",
  fontFamily: "QuicksandBold",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "18px",
};

const overviewActivitySx = {
  color: "#1E1E1E",
  fontFamily: "QuicksandMedium",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "16px",
};

const tableHeaderCellSx = {
  fontFamily: "QuicksandBold",
  color: "#000",
  fontSize: 12,
};

const centeredTableHeaderCellSx = {
  ...tableHeaderCellSx,
  textAlign: "center",
};

const diagnosisCodeSx = {
  color: "#1E1E1E",
  fontFamily: "QuicksandBold",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "16px",
};

const diagnosisDescriptionSx = {
  color: "#1E1E1E",
  fontFamily: "QuicksandMedium",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "16px",
};

const iconButtonSx = { color: "black", cursor: "pointer" };

const inlineLinkSx = {
  color: "#0B34D8",
  fontFamily: "QuicksandMedium",
  fontSize: "13px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "18px",
  textDecorationLine: "underline",
  textDecorationStyle: "solid",
  textDecorationSkipInk: "auto",
  textDecorationThickness: "auto",
  textUnderlineOffset: "auto",
  textUnderlinePosition: "from-font",
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
};

const ArchiveActionIcon = () => (
  <SvgIcon sx={{ width: 24, height: 24 }}>
    <path
      d="M20.54 5.23L19.15 3.55C18.88 3.21 18.47 3 18 3H6C5.53 3 5.12 3.21 4.84 3.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V6.5C21 6.02 20.83 5.57 20.54 5.23ZM12 17.5L6.5 12H10V10H14V12H17.5L12 17.5ZM5.12 5L5.93 4H17.93L18.87 5H5.12Z"
      fill="black"
    />
  </SvgIcon>
);

export interface EncounterInterventionsPanelProps {
  patientId: string;
  encounterUuid?: string;
}

const EncounterInterventionsPanel: React.FC<
  EncounterInterventionsPanelProps
> = ({ patientId, encounterUuid }) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const images = React.useContext(ImagesContext);
  const { showToast } = useFlash();

  const [interventions, setInterventions] = React.useState<any[]>([]);
  const [expandedNotesId, setExpandedNotesId] = React.useState<number | null>(
    null,
  );
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmRow, setConfirmRow] = React.useState<{
    id: number;
    status: string;
  } | null>(null);
  const [confirmAction, setConfirmAction] = React.useState<
    "delete" | "archive"
  >("delete");
  const [actionInProgress, setActionInProgress] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  const newInterventionHref = encounterUuid
    ? `/patient_reports/${patientId}/interventions/new?encounter_id=${encodeURIComponent(encounterUuid)}`
    : "";

  const interventionFormHref = (row: any, viewOnly: boolean) => {
    const encounterQuery = encounterUuid
      ? `&encounter_id=${encodeURIComponent(encounterUuid)}`
      : "";
    const base = `/patient_reports/${patientId}/interventions/new?intervention_id=${row.id}${encounterQuery}`;
    return viewOnly ? `${base}&view_only=1` : base;
  };

  const getInterventionData = React.useCallback(() => {
    if (!patientId) return;
    if (!encounterUuid) {
      setInterventions([]);
      setLoading(false);
      return;
    }
    const url = `/interventions/index/${patientId}`;
    setLoading(true);
    fetch(url, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success === false) {
          showToast({
            title: "Interventions Load Error",
            text: result.error || "Failed to load interventions.",
            type: "error",
          });
          setInterventions([]);
        } else {
          const rows = Array.isArray(result?.resource) ? result.resource : [];
          setInterventions(
            rows.filter(
              (row: any) => row.encounter_billing_uuid === encounterUuid,
            ),
          );
        }
      })
      .catch((error) => {
        showToast({
          title: "Interventions Load Error",
          text: error.message || "Failed to load interventions.",
          type: "error",
        });
        setInterventions([]);
      })
      .finally(() => setLoading(false));
  }, [
    patientId,
    encounterUuid,
    authenticationSetting.csrfToken,
    showToast,
  ]);

  React.useEffect(() => {
    getInterventionData();
  }, [getInterventionData]);

  const renderStatus = (row: any) => {
    if (row.is_archived) {
      return <Box sx={archivedBadgeSx}>Archived</Box>;
    }
    const isSigned = row.status === "signed";
    const label = isSigned ? "Signed" : "Pended";
    return <Box sx={isSigned ? signedBadgeSx : pendedBadgeSx}>{label}</Box>;
  };

  const renderReviewRequest = (isReadyForReview: boolean) => (
    <Box
      sx={{
        width: 19,
        height: 19,
        flexShrink: 0,
        borderRadius: "3px",
        border: "2px solid #FF890A",
        backgroundColor: isReadyForReview ? "#FF890A" : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
      }}
    >
      {isReadyForReview ? (
        <CheckIcon sx={{ color: "#FFFFFF", fontSize: 14 }} />
      ) : null}
    </Box>
  );

  const renderNotesPreview = (intervention: any) => {
    const notes = intervention.notes || intervention.notes_preview || "";
    const isExpanded = expandedNotesId === intervention.id;
    const shouldTruncate = notes.length > NOTES_PREVIEW_LENGTH && !isExpanded;
    const displayText = shouldTruncate
      ? notes.slice(0, NOTES_PREVIEW_LENGTH) + "..."
      : notes;

    return (
      <span>
        {displayText}
        {notes.length > NOTES_PREVIEW_LENGTH && (
          <Link
            component="button"
            variant="body2"
            onClick={() =>
              setExpandedNotesId(isExpanded ? null : intervention.id)
            }
            sx={{ ...inlineLinkSx, ml: 0.5 }}
          >
            {isExpanded ? " Show less" : " Read more"}
          </Link>
        )}
      </span>
    );
  };

  const renderMedications = (row: any) => {
    const text =
      row.related_medication != null
        ? String(row.related_medication).trim()
        : "";
    if (!text) {
      return "—";
    }
    return (
      <Box
        component="span"
        sx={{
          ...diagnosisDescriptionSx,
          wordBreak: "break-word",
        }}
      >
        {text}
      </Box>
    );
  };

  const renderRelatedConditionDx = (row: any) => {
    if (!row.related_condition_code && !row.related_condition_description) {
      return "—";
    }

    if (row.related_condition_code && row.related_condition_description) {
      return (
        <Box component="span">
          <Box component="span" sx={diagnosisCodeSx}>
            {row.related_condition_code}
          </Box>
          <Box component="span" sx={diagnosisDescriptionSx}>
            {" - "}
            {row.related_condition_description}
          </Box>
        </Box>
      );
    }

    return row.related_condition_code ? (
      <Box component="span" sx={diagnosisCodeSx}>
        {row.related_condition_code}
      </Box>
    ) : (
      <Box component="span" sx={diagnosisDescriptionSx}>
        {row.related_condition_description}
      </Box>
    );
  };

  const renderViewEditAction = (row: any) =>
    row.status === "pended" ? (
      <a title="Edit" href={interventionFormHref(row, false)}>
        <CreateIcon sx={iconButtonSx} />
      </a>
    ) : (
      <a title="View" href={interventionFormHref(row, true)}>
        <VisibilityIcon sx={iconButtonSx} />
      </a>
    );

  const openConfirm = (row: any, action: "delete" | "archive") => {
    setConfirmRow({ id: row.id, status: row.status });
    setConfirmAction(action);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (!actionInProgress) {
      setConfirmOpen(false);
      setConfirmRow(null);
    }
  };

  const handleConfirmRemove = () => {
    if (!confirmRow || !patientId) return;
    setActionInProgress(true);
    const isDelete = confirmAction === "delete";
    const url = isDelete
      ? `/interventions/${confirmRow.id}`
      : `/interventions/${confirmRow.id}/archive`;
    const method = isDelete ? "DELETE" : "PATCH";
    fetch(url, {
      method,
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success) {
          showToast({
            title: isDelete ? "Intervention deleted" : "Intervention archived",
            text:
              result.message ||
              (isDelete
                ? "The intervention has been permanently removed."
                : "The intervention has been archived."),
            type: "success",
          });
          getInterventionData();
          setConfirmOpen(false);
          setConfirmRow(null);
        } else {
          showToast({
            title: isDelete ? "Delete failed" : "Archive failed",
            text:
              result.error ||
              result.message ||
              "Action could not be completed.",
            type: "error",
          });
        }
      })
      .catch((err) => {
        showToast({
          title: isDelete ? "Delete failed" : "Archive failed",
          text: err.message || "Action could not be completed.",
          type: "error",
        });
      })
      .finally(() => setActionInProgress(false));
  };

  const renderArchiveDeleteAction = (row: any) => {
    if (row.is_archived) {
      return null;
    }
    if (row.status === "pended") {
      return (
        <Tooltip title="Delete" arrow>
          <Link
            component="button"
            onClick={() => openConfirm(row, "delete")}
            sx={iconButtonSx}
          >
            <DeleteIcon sx={{ fontSize: 24 }} />
          </Link>
        </Tooltip>
      );
    }
    return (
      <Tooltip title="Archive" arrow>
        <Link
          component="button"
          onClick={() => openConfirm(row, "archive")}
          sx={iconButtonSx}
        >
          <ArchiveActionIcon />
        </Link>
      </Tooltip>
    );
  };

  const confirmTitle =
    confirmAction === "delete"
      ? "Delete Intervention?"
      : "Archive Intervention?";
  const confirmMessage =
    confirmAction === "delete"
      ? "Are you sure you want to delete this intervention? This cannot be undone."
      : "Are you sure you want to archive this intervention? It will be removed from the active list.";
  const confirmMessageLines = confirmMessage.split("\n").map((line, index) => (
    <React.Fragment key={index}>
      {line}
      <br />
    </React.Fragment>
  ));

  if (!patientId) {
    return null;
  }

  return (
    <>
      <DeleteModal
        successModalOpen={confirmOpen}
        setSuccessModalOpen={setConfirmOpen}
        successHeader={confirmTitle}
        successContent={confirmMessageLines}
        successCallback={handleConfirmRemove}
        closeCallback={closeConfirm}
        confirmButtonText="Continue"
        isLoading={actionInProgress}
        width="500px"
        buttonWidth="154px"
        padding="24px 120px"
      />
      <Grid container className="panel-container" borderRadius="4px">
        <Grid item xs={12}>
          <Grid container className="panel-show-container">
            <Grid container className="panel-information-container">
              <Grid container direction="row" className="admin-header">
                <Grid item xs={12} className="box-header">
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    paddingX={3}
                    paddingY={1}
                    alignItems="center"
                    display="flex"
                  >
                    <Grid item>
                      <p className="panel-header">Interventions</p>
                    </Grid>
                    <div className="row">
                      <Link
                        className="action-link row"
                        href={newInterventionHref || undefined}
                        onClick={(event) => {
                          if (!encounterUuid) event.preventDefault();
                        }}
                      >
                        <img
                          src={images.add_icon}
                          alt=""
                          className="action-icon-image"
                          style={{ width: 31.466, maxHeight: 32 }}
                        />
                        <span
                          className="app-user-text disable-pointer"
                          style={{ marginLeft: "8px" }}
                        >
                          New Intervention
                        </span>
                      </Link>
                    </div>
                  </Stack>
                </Grid>
              </Grid>
              <Grid container className="grey-container">
                <Grid container className="tabs-container">
                  <Grid
                    item
                    xs={12}
                    className="panel-body tab-body-container"
                    padding={0}
                    sx={{
                      border: "0px !important",
                      paddingBottom: "0px !important",
                    }}
                  >
                    {loading ? (
                      <p
                        style={{
                          textAlign: "center",
                          fontSize: "13px",
                          fontWeight: 500,
                          lineHeight: "18px",
                        }}
                      >
                        Loading interventions...
                      </p>
                    ) : interventions.length === 0 ? (
                      <p
                        style={{
                          textAlign: "center",
                          fontSize: "13px",
                          fontWeight: 500,
                          lineHeight: "18px",
                        }}
                      >
                        {encounterUuid ? (
                          "No interventions linked to this encounter yet. You can create your first interventions "
                        ) : (
                          "Save this encounter first to link interventions."
                        )}
                        {encounterUuid && (
                          <Link
                            href={newInterventionHref}
                            sx={{
                              color: "#3F51B5",
                              textDecoration: "underline",
                              cursor: "pointer",
                              fontSize: "13px",
                              fontWeight: 500,
                              lineHeight: "18px",
                            }}
                          >
                            here!
                          </Link>
                        )}
                      </p>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead className="table-header-box">
                            <TableRow>
                              <TableCell sx={tableHeaderCellSx} width="16%">
                                Overview
                              </TableCell>
                              <TableCell sx={tableHeaderCellSx} width="15%">
                                Related Condition/Dx
                              </TableCell>
                              <TableCell sx={tableHeaderCellSx} width="14%">
                                Medications
                              </TableCell>
                              <TableCell sx={tableHeaderCellSx} width="20%">
                                Notes
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={centeredTableHeaderCellSx}
                                width="11%"
                              >
                                Status
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={centeredTableHeaderCellSx}
                                width="13%"
                              >
                                Review Request
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={centeredTableHeaderCellSx}
                              >
                                History
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={centeredTableHeaderCellSx}
                              >
                                View/Edit
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={centeredTableHeaderCellSx}
                              >
                                Archive
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {interventions.map((row) => (
                              <TableRow
                                key={row.id}
                                sx={{
                                  "& td, & th": { borderBottom: "none" },
                                }}
                              >
                                <TableCell className="date-data">
                                  <Stack spacing={0.5}>
                                    <Box component="span" sx={overviewDateSx}>
                                      {row.occurred_on_date}
                                    </Box>
                                    <Box component="span" sx={overviewTypeSx}>
                                      {row.intervention_type || "—"}
                                    </Box>
                                    <Box
                                      component="span"
                                      sx={overviewActivitySx}
                                    >
                                      {row.activity || "—"}
                                    </Box>
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  {renderRelatedConditionDx(row)}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 260 }}>
                                  {renderMedications(row)}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 340 }}>
                                  {row.notes ? renderNotesPreview(row) : "—"}
                                </TableCell>
                                <TableCell align="center">
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {renderStatus(row)}
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                      pl: "10px",
                                    }}
                                  >
                                    {renderReviewRequest(
                                      !!row.is_ready_for_review,
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="History" arrow>
                                    <AccessTimeOutlinedIcon
                                      style={iconButtonSx}
                                    />
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="center">
                                  {renderViewEditAction(row)}
                                </TableCell>
                                <TableCell align="center">
                                  {renderArchiveDeleteAction(row)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default EncounterInterventionsPanel;
